import React, { useCallback, useContext, useEffect, useState } from 'react'
import { DataResult, process, State } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridDataStateChangeEvent,
  GridDetailRowProps,
  GridExpandChangeEvent,
  GridHeaderSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { ResolveFormSubmitValues } from 'api/WorldCheck/Types/ResolveFormSubmitValues'
import { ScreeningResultDto, ScreeningResultsDto } from 'api/WorldCheck/Types/ScreeningResultsDto'
import { Loading } from 'components/Loading'
import { errorToast, ToastContext } from 'components/Toast/Toast'
import styled from 'styled-components'
import { TicketsApiClient } from 'utils/api/ticketsApi'
import { useFetchOne } from 'utils/api/useFetch'
import { WorldCheckApiClient } from 'utils/api/worldCheckApi'
import { formatLocalDateUTC } from 'utils/date'

import { ClientDetailsNoMatch } from './ClientDetailsNoMatch'
import { WorldCheckResolveForm } from './ClientDetailsWorldCheckResolveForm'
import { ClientDetailsWorldCheckResultDetails } from './ClientDetailsWorldCheckResultDetails'
import { ColumnMenu } from './GridColumnMenu'
import { InformedComplianceConfirmationModal } from './InformedComplianceConfirmationModal'
import { ScreenResultTransformedDto } from './ScreenResultTransformedDto'
import { WorldCheckLogo } from './WorldCheckLogo'

const Container = styled.div<{ fullHeight: boolean }>(
  ({ fullHeight }) => `
    margin: 0 2.5rem 2.5rem 2.5rem;
    min-height: ${fullHeight ? '30rem' : '5rem'};
    .alias {
        &_name {
            color: #121212;
        }
        &_alias {
            color: #8c8c8c;
        }
    }
    .link-type {
        text-decoration: underline;
        cursor: pointer;
    }
`
)

interface Props {
  clientId?: string
  worldCheckApiClient: WorldCheckApiClient
  ticketsApiClient: TicketsApiClient
  enableOngoingScreening?: boolean
  typeId?: number
  caseId?: string
  isFromHistory?: boolean

  onResolve(): void

  onFailure(): void

  onWorldCheckNoRecordsFoundResolve?(): void

  affiliateWorldCheck?(): Promise<ScreeningResultsDto | undefined>
}

export enum WorldCheckStatusViewType {
  Undefined = 0,
  Positive = 1,
  Possible = 2,
  False = 3,
  Unspecified = 4,
}

const WorldCheckDetails: React.FC<GridDetailRowProps & { createdAt?: string }> = ({ dataItem }) => {
  const data: ScreeningResultDto = dataItem.dataItem
  return (
    <section>
      <p>
        <b>Status</b>: {data?.status?.name}
      </p>
      <p>
        <b>Risk</b>: {data?.riskLevel?.name}
      </p>
      <p>
        <b>Reason</b>: {data?.reason?.name}
      </p>
      <p>
        <b>Remark</b>: {data?.remark}
      </p>
    </section>
  )
}

const createDataState = (dataState: State, data: ScreenResultTransformedDto[]) => {
  return {
    result: process(data.slice(0), dataState),
    dataState,
  }
}

export const ClientDetailsWorldCheckModal: React.FC<Props> = ({
  enableOngoingScreening,
  worldCheckApiClient,
  ticketsApiClient,
  clientId,
  caseId,
  typeId,
  isFromHistory,
  onResolve,
  onFailure,
  onWorldCheckNoRecordsFoundResolve,
  affiliateWorldCheck,
}) => {
  const fetchData = useCallback(async () => {
    if (clientId && !isFromHistory) {
      return worldCheckApiClient.sendClientForWorldCheckVerification({
        clientId,
        enableOngoingScreening,
        typeId,
      })
    }
    if (caseId) {
      return worldCheckApiClient.getWorldCheckScreeningById(caseId)
    }
  }, [clientId, isFromHistory, caseId, enableOngoingScreening]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data, isLoading } = useFetchOne(affiliateWorldCheck ?? fetchData)
  const [stateScreenings, setScreenings] = useState<ScreenResultTransformedDto[]>([])
  const [dataState, setDataState] = useState<{
    result: DataResult
    dataState: State
  }>(
    createDataState(
      {
        skip: 0,
      },
      []
    )
  )
  const setToast = useContext(ToastContext)
  const fetchCases = useCallback(
    (id: string) => worldCheckApiClient.getWorldCheckScreeningById(id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const [detailScreeningRecord, setDetailsScreeningRecord] = useState<null | string>(null)
  const [isInformedComplianceModalOpen, setInformedComplianceModalOpen] = useState(false)
  useEffect(() => {
    if (data) {
      setScreenings(prepareData(data.records))
      setDataState(createDataState(dataState.dataState, prepareData(data.records)))
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (submitValues: ResolveFormSubmitValues) => {
    if (data) {
      const records = dataState.result.data.filter((v) => !!v.selected).map((x) => x.id)
      if (!records.length) {
        setToast(errorToast('Please select at least one result.'))
      } else {
        try {
          const dataToSubmit = { ...submitValues, records }
          await worldCheckApiClient.resolveWorldCheckRecords(data.id, dataToSubmit)
          const screening = await fetchCases(data.id)
          setDataState(createDataState(dataState.dataState, prepareData(screening.records)))
          setScreenings(prepareData(screening.records))
          onResolve()
        } catch (error) {
          console.error(error)
          onFailure()
        }
      }
    }
  }

  const headerSelectionValue = (): boolean => {
    if ((dataState.result.data || []).length <= 0) {
      return false
    }
    return (dataState.result.data || []).findIndex((dataItem) => !dataItem.selected) === -1
  }

  const handleSelectionChange = ({ dataItem }: { dataItem: ScreenResultTransformedDto }) => {
    const modifiedData = (stateScreenings || []).map((item) => {
      const foundInState = dataState.result.data.find((x) => x.id === item.id && x.selected)
      if (foundInState && foundInState.id !== dataItem.id) {
        return {
          ...item,
          selected: foundInState.selected,
        }
      }
      if (item.id === dataItem.id) {
        return {
          ...item,
          selected: !dataItem.selected,
        }
      }
      return item
    })
    setDataState(createDataState(dataState.dataState, modifiedData))
  }

  const prepareData = (result: ScreeningResultDto[]): ScreenResultTransformedDto[] => {
    return result.reduce<ScreenResultTransformedDto[]>((previousValue, currentValue) => {
      const location = currentValue.result?.location
      const dateOfBirth = currentValue.result?.dateOfBirth
      const nationality = currentValue.result?.nationality
      const primaryName = currentValue.result?.primaryName

      const placeOfBirth = currentValue.result?.placeOfBirth
      const transformedDto: ScreenResultTransformedDto = {
        referenceId: currentValue.referenceId,
        id: currentValue.id,
        category: currentValue.result?.categories,
        countryLocation: location || null,
        createdAt: formatLocalDateUTC(currentValue.result?.creationDate, true),
        dateOfBirth: dateOfBirth || null,
        fullName: primaryName || null,
        matchedNameType: currentValue.result?.matchedNameType,
        matchedTerm: currentValue.result?.matchedTerm,
        matchStrength: currentValue.result?.matchStrength,
        nationality: nationality || null,
        placeOfBirth: placeOfBirth || null,
        status: currentValue.status,
        reason: currentValue.reason,
        remark: currentValue.remark,
        riskLevel: currentValue.riskLevel,
        profile: currentValue.profile,
      }
      return [...previousValue, transformedDto]
    }, [])
  }

  const expandChange = (event: GridExpandChangeEvent) => {
    if (data) {
      event.dataItem[event.target.props.expandField as string] = event.value
      setDataState(createDataState(dataState.dataState, stateScreenings))
    }
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    const selected = (stateScreenings || []).map((item) => {
      return { ...item, selected: isChecked }
    })
    setDataState(
      createDataState({ take: dataState.dataState.take, skip: dataState.dataState.skip }, selected)
    )
  }

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const createdDataState = createDataState(event.dataState, stateScreenings)
    setDataState(createdDataState)
  }

  const showInformedComplianceModal = () => {
    setInformedComplianceModalOpen(true)
  }

  const hideInformedComplianceModal = () => {
    setInformedComplianceModalOpen(false)
  }

  if (detailScreeningRecord)
    return (
      <ClientDetailsWorldCheckResultDetails
        worldCheckApiClient={worldCheckApiClient}
        onClose={() => setDetailsScreeningRecord(null)}
        referenceId={detailScreeningRecord}
      />
    )

  return (
    <React.Fragment>
      {isInformedComplianceModalOpen && (
        <InformedComplianceConfirmationModal
          clientId={clientId}
          ticketsApiClient={ticketsApiClient}
          onCancel={hideInformedComplianceModal}
          visible={isInformedComplianceModalOpen}
        />
      )}
      <Container fullHeight={!!dataState.result.data.length}>
        <Loading isLoading={isLoading} modalView={true}>
          {!dataState.result.data.length ? (
            <ClientDetailsNoMatch
              onWorldCheckNoRecordsFoundResolve={onWorldCheckNoRecordsFoundResolve}
            />
          ) : (
            <React.Fragment>
              <WorldCheckLogo />
              <WorldCheckResolveForm
                onSubmit={resolve}
                screeningId={data?.id}
                showInformedComplianceModal={showInformedComplianceModal}
              />
              <Grid
                resizable
                data={dataState.result}
                {...dataState.dataState}
                style={{ zIndex: 0 }}
                selectedField='selected'
                detail={(dataItem) => (
                  <WorldCheckDetails
                    dataIndex={dataItem.dataIndex}
                    dataItem={dataItem}
                    createdAt={data?.createdAt}
                  />
                )}
                pageable={{ pageSizes: [10, 20, 50, 100, 500, 1000] }}
                expandField='expanded'
                onExpandChange={expandChange}
                onSelectionChange={handleSelectionChange}
                onHeaderSelectionChange={handleHeaderSelectionChange}
                onDataStateChange={dataStateChange}
              >
                <Column
                  field='selected'
                  width='50px'
                  headerSelectionValue={headerSelectionValue()}
                />
                <Column title='Status' field='status.name' columnMenu={ColumnMenu} />
                <Column
                  title='Name'
                  field='fullName'
                  cell={(gridCellProps: GridCellProps) => {
                    return (
                      <td
                        className='link-type'
                        onClick={() =>
                          setDetailsScreeningRecord(gridCellProps.dataItem.referenceId)
                        }
                      >
                        {gridCellProps.dataItem.fullName}
                      </td>
                    )
                  }}
                />
                <Column
                  title='Matched Alis (name)'
                  field='matchedNameType'
                  columnMenu={ColumnMenu}
                />
                <Column title='Matched Alias (term)' field='matchedTerm' columnMenu={ColumnMenu} />
                <Column title='Match Strength' field='matchStrength' columnMenu={ColumnMenu} />
                <Column title='Date of Birth' field='dateOfBirth' />
                <Column title='Place of Birth' field='placeOfBirth' columnMenu={ColumnMenu} />
                <Column title='Nationality' field='nationality' columnMenu={ColumnMenu} />
                <Column title='Country Location' field='countryLocation' columnMenu={ColumnMenu} />
                <Column title='Category' field='category' columnMenu={ColumnMenu} />
                <Column title='Creation Date' field='createdAt' />
              </Grid>
            </React.Fragment>
          )}
        </Loading>
      </Container>
    </React.Fragment>
  )
}
